import type { CombatCamera } from './camera';

// Pointer gestures for the combat camera, attached to the canvas host
// element rather than to PIXI's event system — we want them regardless of
// what's under the cursor, and the host already sets `touch-action: none`
// so the browser won't claim any of them.
//
// Gesture map:
//   1 pointer drag  → pan
//   2 pointer pinch → zoom about the midpoint
//   wheel           → zoom about the cursor
//   double tap      → toggle fit / close-up at that point
//
// Note this is the OPPOSITE arbitration from the tavern rails, and
// deliberately so: there, `touch-action: pan-x` hands horizontal swipes to
// the browser because the rails are real scroll containers. Here nothing
// scrolls and there is nothing to drag, so the camera can claim every
// gesture outright.

const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_SLOP = 24;
/** Movement past this cancels a tap, so a small pan never zooms. */
const TAP_SLOP = 10;

export function attachCameraGestures(
  host: HTMLElement,
  camera: CombatCamera,
  now: () => number = () => performance.now(),
): () => void {
  // Active pointers by id, in element-local coordinates.
  const pointers = new Map<number, { x: number; y: number }>();
  let lastPinchDist = 0;
  let lastTapAt = 0;
  let lastTapX = 0;
  let lastTapY = 0;
  let moved = 0;

  const local = (e: PointerEvent) => {
    const r = host.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const pinchDistance = () => {
    const pts = [...pointers.values()];
    if (pts.length < 2) return 0;
    const dx = pts[0].x - pts[1].x;
    const dy = pts[0].y - pts[1].y;
    return Math.hypot(dx, dy);
  };

  const pinchMidpoint = () => {
    const pts = [...pointers.values()];
    return {
      x: (pts[0].x + pts[1].x) / 2,
      y: (pts[0].y + pts[1].y) / 2,
    };
  };

  function onPointerDown(e: PointerEvent) {
    const p = local(e);
    pointers.set(e.pointerId, p);
    host.setPointerCapture?.(e.pointerId);
    if (pointers.size === 1) {
      moved = 0;
    } else if (pointers.size === 2) {
      lastPinchDist = pinchDistance();
    }
  }

  function onPointerMove(e: PointerEvent) {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    const p = local(e);
    pointers.set(e.pointerId, p);

    if (pointers.size >= 2) {
      const dist = pinchDistance();
      if (lastPinchDist > 0 && dist > 0) {
        const mid = pinchMidpoint();
        camera.zoomBy(dist / lastPinchDist, mid.x, mid.y);
      }
      lastPinchDist = dist;
      moved = Infinity; // a pinch is never a tap
      return;
    }

    const dx = p.x - prev.x;
    const dy = p.y - prev.y;
    moved += Math.hypot(dx, dy);
    // Only pan once past the tap slop, so a tap-to-zoom isn't preceded by a
    // jitter pan from an unsteady finger.
    if (moved > TAP_SLOP) camera.panBy(dx, dy);
  }

  function endPointer(e: PointerEvent) {
    const wasSingle = pointers.size === 1;
    pointers.delete(e.pointerId);
    host.releasePointerCapture?.(e.pointerId);
    if (pointers.size < 2) lastPinchDist = 0;

    if (!wasSingle || moved > TAP_SLOP) return;
    // A tap. Pair it with a recent nearby tap to make a double tap.
    const t = now();
    const p = local(e);
    const quick = t - lastTapAt < DOUBLE_TAP_MS;
    const near =
      Math.abs(p.x - lastTapX) < DOUBLE_TAP_SLOP &&
      Math.abs(p.y - lastTapY) < DOUBLE_TAP_SLOP;
    if (quick && near) {
      camera.toggleZoomAt(p.x, p.y);
      lastTapAt = 0;
    } else {
      lastTapAt = t;
      lastTapX = p.x;
      lastTapY = p.y;
    }
  }

  function onWheel(e: WheelEvent) {
    // While locked the camera isn't ours to move, so don't swallow the
    // wheel — preventDefault here would silently block page scroll on
    // desktop, which is the mode that defaults to locked.
    if (camera.isLocked) return;
    e.preventDefault();
    const r = host.getBoundingClientRect();
    // Exponential so trackpads and notched wheels both feel linear.
    camera.zoomBy(Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top);
  }

  host.addEventListener('pointerdown', onPointerDown);
  host.addEventListener('pointermove', onPointerMove);
  host.addEventListener('pointerup', endPointer);
  host.addEventListener('pointercancel', endPointer);
  host.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    host.removeEventListener('pointerdown', onPointerDown);
    host.removeEventListener('pointermove', onPointerMove);
    host.removeEventListener('pointerup', endPointer);
    host.removeEventListener('pointercancel', endPointer);
    host.removeEventListener('wheel', onWheel);
  };
}
